import React, { useState } from 'react';
import { ArrowLeft, CheckCircle, AlertTriangle, Plane, Hotel, Car, Utensils, Calendar, Download, Phone, MapPin, Clock, FileText, PlusIcon, EditIcon, ShareIcon, RefreshCwIcon, StickyNoteIcon, XIcon, PencilIcon } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
interface TimelineEvent {
  time: string;
  icon: React.ElementType;
  color: string;
  title: string;
  details: string;
  cost: string | null;
}
interface TimelineDay {
  day: string;
  events: TimelineEvent[];
}
export function TripDetailPage() {
  const {
    id
  } = useParams();
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [tripNotes, setTripNotes] = useState<string[]>(['Window seat preferred', 'Vegetarian meals requested']);
  const [newNote, setNewNote] = useState('');
  const [addForm, setAddForm] = useState({
    type: 'Meeting',
    title: '',
    date: '',
    time: '',
    location: '',
    cost: '',
    notes: ''
  });
  const showToast = (message: string) => {
    setToastMessage(message);
    setTimeout(() => setToastMessage(null), 3000);
  };
  const trip = {
    title: 'Q4 Strategy Meeting',
    destination: 'San Francisco, CA',
    dates: 'Nov 12 - Nov 15, 2023',
    status: 'Confirmed',
    totalCost: '$2,003.00',
    traveler: 'Alex Morgan',
    purpose: 'Quarterly planning with executive team',
    bookingRef: 'TR-8842-SF',
    policyStatus: 'compliant'
  };
  const getIconForType = (type: string): React.ElementType => {
    switch (type) {
      case 'Meeting':
        return Calendar;
      case 'Meal':
        return Utensils;
      case 'Transport':
        return Car;
      case 'Activity':
        return MapPin;
      case 'Note':
        return FileText;
      default:
        return Calendar;
    }
  };
  const getColorForType = (type: string): string => {
    switch (type) {
      case 'Meeting':
        return 'bg-slate-100 text-slate-600';
      case 'Meal':
        return 'bg-emerald-100 text-emerald-600';
      case 'Transport':
        return 'bg-amber-100 text-amber-600';
      case 'Activity':
        return 'bg-blue-100 text-blue-600';
      case 'Note':
        return 'bg-purple-100 text-purple-600';
      default:
        return 'bg-slate-100 text-slate-600';
    }
  };
  const [customEvents, setCustomEvents] = useState<TimelineDay[]>([]);
  const initialTimeline: TimelineDay[] = [{
    day: 'Day 1 — Nov 12',
    events: [{
      time: '08:00 AM',
      icon: Plane,
      color: 'bg-blue-100 text-blue-600',
      title: 'Flight UA 4521',
      details: 'JFK → SFO • Economy • Seat 12A',
      cost: '$450'
    }, {
      time: '02:00 PM',
      icon: Hotel,
      color: 'bg-purple-100 text-purple-600',
      title: 'Check-in at Marriott Union Square',
      details: 'Room 1204 • 3 Nights',
      cost: '$280/night'
    }, {
      time: '06:00 PM',
      icon: Car,
      color: 'bg-amber-100 text-amber-600',
      title: 'Ground Transport',
      details: 'Uber to dinner venue',
      cost: '$24'
    }]
  }, {
    day: 'Day 2 — Nov 13',
    events: [{
      time: '09:00 AM',
      icon: Calendar,
      color: 'bg-slate-100 text-slate-600',
      title: 'Conference Registration',
      details: 'Moscone Center',
      cost: null
    }, {
      time: '12:00 PM',
      icon: Utensils,
      color: 'bg-emerald-100 text-emerald-600',
      title: 'Lunch Meeting',
      details: 'Client: TechCorp',
      cost: '$65'
    }]
  }, {
    day: 'Day 3 — Nov 14',
    events: [{
      time: '09:00 AM',
      icon: Calendar,
      color: 'bg-slate-100 text-slate-600',
      title: 'Workshop Sessions',
      details: 'Strategy & Planning',
      cost: null
    }, {
      time: '07:00 PM',
      icon: Utensils,
      color: 'bg-emerald-100 text-emerald-600',
      title: 'Team Dinner',
      details: 'The Stinking Rose',
      cost: '$120'
    }]
  }, {
    day: 'Day 4 — Nov 15',
    events: [{
      time: '10:00 AM',
      icon: Hotel,
      color: 'bg-purple-100 text-purple-600',
      title: 'Check-out',
      details: 'Marriott Union Square',
      cost: null
    }, {
      time: '02:00 PM',
      icon: Plane,
      color: 'bg-blue-100 text-blue-600',
      title: 'Flight UA 4522',
      details: 'SFO → JFK • Economy • Seat 14C',
      cost: '$480'
    }]
  }];
  const allTimeline = [...initialTimeline, ...customEvents];
  const handleAddItem = () => {
    if (!addForm.title || !addForm.time) return;
    const icon = getIconForType(addForm.type);
    const color = getColorForType(addForm.type);
    const dayLabel = addForm.date ? `Added — ${addForm.date}` : 'Custom Items';
    const newEvent: TimelineEvent = {
      time: addForm.time,
      icon,
      color,
      title: addForm.title,
      details: [addForm.location, addForm.notes].filter(Boolean).join(' • ') || addForm.type,
      cost: addForm.cost ? `$${addForm.cost}` : null
    };
    setCustomEvents(prev => {
      const existingDay = prev.find(d => d.day === dayLabel);
      if (existingDay) {
        return prev.map(d => d.day === dayLabel ? {
          ...d,
          events: [...d.events, newEvent]
        } : d);
      }
      return [...prev, {
        day: dayLabel,
        events: [newEvent]
      }];
    });
    setAddForm({
      type: 'Meeting',
      title: '',
      date: '',
      time: '',
      location: '',
      cost: '',
      notes: ''
    });
    setShowAddModal(false);
    showToast('Item added to your itinerary');
  };
  const handleAddNote = () => {
    if (!newNote.trim()) return;
    setTripNotes(prev => [...prev, newNote.trim()]);
    setNewNote('');
    setShowNoteModal(false);
    showToast('Note saved to trip');
  };
  return <div className="space-y-6" data-id="element-3095">
      {/* Toast */}
      {toastMessage && <div className="fixed top-20 right-6 z-50 animate-in slide-in-from-top-2" data-id="element-3096">
          <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg p-4 shadow-lg flex items-center gap-3" data-id="element-3097">
            <CheckCircle className="h-5 w-5 text-emerald-600 flex-shrink-0" data-id="element-3098" />
            <p className="font-medium text-sm" data-id="element-3099">{toastMessage}</p>
            <button onClick={() => setToastMessage(null)} className="ml-2 text-emerald-500 hover:text-emerald-700" data-id="element-3100">
              <XIcon className="h-4 w-4" data-id="element-3101" />
            </button>
          </div>
        </div>}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-3102">
        <div className="flex items-center gap-4" data-id="element-3103">
          <Link to="/trips" className="p-2 rounded-full hover:bg-slate-100 text-slate-500" data-id="element-3104">
            <ArrowLeft className="h-5 w-5" data-id="element-3105" />
          </Link>
          <div data-id="element-3106">
            <div className="flex items-center gap-3" data-id="element-3107">
              <h2 className="text-2xl font-bold text-slate-800" data-id="element-3108">
                {trip.title}
              </h2>
              <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800 flex items-center gap-1" data-id="element-3109">
                <CheckCircle className="h-3 w-3" data-id="element-3110" /> {trip.status}
              </span>
            </div>
            <p className="text-slate-500 text-sm mt-1" data-id="element-3111">
              Ref: {trip.bookingRef}
            </p>
          </div>
        </div>
        <div className="text-right" data-id="element-3112">
          <p className="text-sm text-slate-500" data-id="element-3113">Total Cost</p>
          <p className="text-2xl font-bold text-slate-900" data-id="element-3114">{trip.totalCost}</p>
        </div>
      </div>

      {/* Manage Trip Actions */}
      <div className="flex flex-wrap gap-2" data-id="element-3115">
        <button onClick={() => setShowAddModal(true)} className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 transition-colors" data-id="element-3116">
          <PlusIcon className="h-4 w-4" data-id="element-3117" /> Add to Itinerary
        </button>
        <button onClick={() => showToast('Change request submitted to travel desk')} className="flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors" data-id="element-3118">
          <RefreshCwIcon className="h-4 w-4" data-id="element-3119" /> Request Change
        </button>
        <button onClick={() => setShowNoteModal(true)} className="flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors" data-id="element-3120">
          <StickyNoteIcon className="h-4 w-4" data-id="element-3121" /> Add Note
        </button>
        <button onClick={() => showToast('Itinerary link copied to clipboard')} className="flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors" data-id="element-3122">
          <ShareIcon className="h-4 w-4" data-id="element-3123" /> Share Itinerary
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-3124">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6" data-id="element-3125">
          {/* Overview Card */}
          <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm" data-id="element-3126">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3127">
              <div className="space-y-4" data-id="element-3128">
                <div className="flex items-start gap-3" data-id="element-3129">
                  <MapPin className="h-5 w-5 text-slate-400 mt-0.5" data-id="element-3130" />
                  <div data-id="element-3131">
                    <p className="text-sm font-medium text-slate-500" data-id="element-3132">
                      Destination
                    </p>
                    <p className="font-semibold text-slate-900" data-id="element-3133">
                      {trip.destination}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-3134">
                  <Calendar className="h-5 w-5 text-slate-400 mt-0.5" data-id="element-3135" />
                  <div data-id="element-3136">
                    <p className="text-sm font-medium text-slate-500" data-id="element-3137">Dates</p>
                    <p className="font-semibold text-slate-900" data-id="element-3138">{trip.dates}</p>
                  </div>
                </div>
              </div>
              <div className="space-y-4" data-id="element-3139">
                <div className="flex items-start gap-3" data-id="element-3140">
                  <FileText className="h-5 w-5 text-slate-400 mt-0.5" data-id="element-3141" />
                  <div data-id="element-3142">
                    <p className="text-sm font-medium text-slate-500" data-id="element-3143">
                      Purpose
                    </p>
                    <p className="font-semibold text-slate-900" data-id="element-3144">
                      {trip.purpose}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-3145">
                  <CheckCircle className="h-5 w-5 text-emerald-500 mt-0.5" data-id="element-3146" />
                  <div data-id="element-3147">
                    <p className="text-sm font-medium text-slate-500" data-id="element-3148">
                      Policy Status
                    </p>
                    <p className="font-semibold text-emerald-700" data-id="element-3149">Compliant</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Timeline */}
          <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm" data-id="element-3150">
            <div className="flex items-center justify-between mb-6" data-id="element-3151">
              <h3 className="font-bold text-lg text-slate-800" data-id="element-3152">Itinerary</h3>
              <button onClick={() => setShowAddModal(true)} className="flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700" data-id="element-3153">
                <PlusIcon className="h-3.5 w-3.5" data-id="element-3154" /> Add Item
              </button>
            </div>
            <div className="space-y-8" data-id="element-3155">
              {allTimeline.map((day, dayIdx) => <div key={dayIdx} className="relative" data-id="element-3156">
                  <div className="sticky top-0 bg-white z-10 py-2 mb-4 border-b border-slate-100" data-id="element-3157">
                    <h4 className="font-semibold text-slate-900" data-id="element-3158">{day.day}</h4>
                  </div>
                  <div className="ml-4 space-y-8 border-l-2 border-slate-100 pl-8 pb-4" data-id="element-3159">
                    {day.events.map((event, idx) => <div key={idx} className="relative group" data-id="element-3160">
                        <div className={`absolute -left-[41px] top-0 h-8 w-8 rounded-full border-4 border-white flex items-center justify-center ${event.color}`} data-id="element-3161">
                          <event.icon className="h-4 w-4" data-id="element-3162" />
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2" data-id="element-3163">
                          <div className="flex-1" data-id="element-3164">
                            <p className="text-xs font-medium text-slate-500 mb-0.5" data-id="element-3165">
                              {event.time}
                            </p>
                            <h5 className="font-semibold text-slate-900" data-id="element-3166">
                              {event.title}
                            </h5>
                            <p className="text-sm text-slate-600" data-id="element-3167">
                              {event.details}
                            </p>
                          </div>
                          <div className="flex items-center gap-2" data-id="element-3168">
                            {event.cost && <span className="text-sm font-medium text-slate-900 bg-slate-50 px-2 py-1 rounded" data-id="element-3169">
                                {event.cost}
                              </span>}
                            <button onClick={() => showToast('Edit request sent to travel desk')} className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-all" title="Request edit" data-id="element-3170">
                              <PencilIcon className="h-3.5 w-3.5" data-id="element-3171" />
                            </button>
                          </div>
                        </div>
                      </div>)}
                  </div>
                </div>)}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6" data-id="element-3172">
          {/* Cost Breakdown */}
          <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm" data-id="element-3173">
            <h3 className="font-bold text-slate-800 mb-4" data-id="element-3174">Cost Breakdown</h3>
            <div className="space-y-3" data-id="element-3175">
              <div className="flex justify-between text-sm" data-id="element-3176">
                <span className="text-slate-600" data-id="element-3177">Flights</span>
                <span className="font-medium text-slate-900" data-id="element-3178">$930.00</span>
              </div>
              <div className="flex justify-between text-sm" data-id="element-3179">
                <span className="text-slate-600" data-id="element-3180">Hotel</span>
                <span className="font-medium text-slate-900" data-id="element-3181">$840.00</span>
              </div>
              <div className="flex justify-between text-sm" data-id="element-3182">
                <span className="text-slate-600" data-id="element-3183">Ground Transport</span>
                <span className="font-medium text-slate-900" data-id="element-3184">$48.00</span>
              </div>
              <div className="flex justify-between text-sm" data-id="element-3185">
                <span className="text-slate-600" data-id="element-3186">Meals</span>
                <span className="font-medium text-slate-900" data-id="element-3187">$185.00</span>
              </div>
              <div className="border-t border-slate-100 pt-3 flex justify-between font-bold" data-id="element-3188">
                <span className="text-slate-900" data-id="element-3189">Total</span>
                <span className="text-blue-600" data-id="element-3190">$2,003.00</span>
              </div>
            </div>
          </div>

          {/* Documents */}
          <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm" data-id="element-3191">
            <h3 className="font-bold text-slate-800 mb-4" data-id="element-3192">Documents</h3>
            <div className="space-y-3" data-id="element-3193">
              <button onClick={() => showToast('Downloading E-Ticket.pdf...')} className="w-full flex items-center justify-between p-3 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors group" data-id="element-3194">
                <div className="flex items-center gap-3" data-id="element-3195">
                  <FileText className="h-5 w-5 text-slate-400 group-hover:text-blue-500" data-id="element-3196" />
                  <span className="text-sm font-medium text-slate-700" data-id="element-3197">
                    E-Ticket.pdf
                  </span>
                </div>
                <Download className="h-4 w-4 text-slate-400" data-id="element-3198" />
              </button>
              <button onClick={() => showToast('Downloading Hotel_Conf.pdf...')} className="w-full flex items-center justify-between p-3 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors group" data-id="element-3199">
                <div className="flex items-center gap-3" data-id="element-3200">
                  <FileText className="h-5 w-5 text-slate-400 group-hover:text-blue-500" data-id="element-3201" />
                  <span className="text-sm font-medium text-slate-700" data-id="element-3202">
                    Hotel_Conf.pdf
                  </span>
                </div>
                <Download className="h-4 w-4 text-slate-400" data-id="element-3203" />
              </button>
              <button onClick={() => showToast('Downloading Itinerary.pdf...')} className="w-full flex items-center justify-between p-3 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors group" data-id="element-3204">
                <div className="flex items-center gap-3" data-id="element-3205">
                  <FileText className="h-5 w-5 text-slate-400 group-hover:text-blue-500" data-id="element-3206" />
                  <span className="text-sm font-medium text-slate-700" data-id="element-3207">
                    Itinerary.pdf
                  </span>
                </div>
                <Download className="h-4 w-4 text-slate-400" data-id="element-3208" />
              </button>
            </div>
          </div>

          {/* Emergency Contacts */}
          <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm" data-id="element-3209">
            <h3 className="font-bold text-slate-800 mb-4" data-id="element-3210">
              Emergency Contacts
            </h3>
            <div className="space-y-4" data-id="element-3211">
              <div className="flex items-start gap-3" data-id="element-3212">
                <div className="bg-red-50 p-2 rounded-full text-red-600" data-id="element-3213">
                  <Phone className="h-4 w-4" data-id="element-3214" />
                </div>
                <div data-id="element-3215">
                  <p className="text-sm font-bold text-slate-900" data-id="element-3216">
                    Local Emergency
                  </p>
                  <p className="text-sm text-slate-600" data-id="element-3217">911 (USA)</p>
                </div>
              </div>
              <div className="flex items-start gap-3" data-id="element-3218">
                <div className="bg-blue-50 p-2 rounded-full text-blue-600" data-id="element-3219">
                  <Phone className="h-4 w-4" data-id="element-3220" />
                </div>
                <div data-id="element-3221">
                  <p className="text-sm font-bold text-slate-900" data-id="element-3222">
                    Travel Desk
                  </p>
                  <p className="text-sm text-slate-600" data-id="element-3223">+1 (800) 555-0199</p>
                </div>
              </div>
            </div>
          </div>

          {/* Trip Notes & Preferences */}
          <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm" data-id="element-3224">
            <div className="flex items-center justify-between mb-4" data-id="element-3225">
              <h3 className="font-bold text-slate-800" data-id="element-3226">Notes & Preferences</h3>
              <button onClick={() => setShowNoteModal(true)} className="text-xs font-medium text-blue-600 hover:text-blue-700 flex items-center gap-1" data-id="element-3227">
                <PlusIcon className="h-3 w-3" data-id="element-3228" /> Add
              </button>
            </div>
            <div className="space-y-2" data-id="element-3229">
              {tripNotes.map((note, idx) => <div key={idx} className="flex items-start gap-2 p-2.5 rounded-lg bg-slate-50 border border-slate-100" data-id="element-3230">
                  <StickyNoteIcon className="h-3.5 w-3.5 text-amber-500 mt-0.5 flex-shrink-0" data-id="element-3231" />
                  <span className="text-sm text-slate-700" data-id="element-3232">{note}</span>
                </div>)}
              {tripNotes.length === 0 && <p className="text-sm text-slate-400 text-center py-4" data-id="element-3233">
                  No notes yet
                </p>}
            </div>
          </div>
        </div>
      </div>

      {/* Add to Itinerary Modal */}
      {showAddModal && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm" data-id="element-3234">
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl max-h-[90vh] overflow-y-auto" data-id="element-3235">
            <div className="flex items-center justify-between mb-6" data-id="element-3236">
              <h3 className="text-lg font-bold text-slate-900" data-id="element-3237">
                Add to Itinerary
              </h3>
              <button onClick={() => setShowAddModal(false)} className="rounded-full p-1 hover:bg-slate-100" data-id="element-3238">
                <XIcon className="h-5 w-5 text-slate-500" data-id="element-3239" />
              </button>
            </div>
            <div className="space-y-4" data-id="element-3240">
              <div data-id="element-3241">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-3242">
                  Type
                </label>
                <select value={addForm.type} onChange={e => setAddForm({
              ...addForm,
              type: e.target.value
            })} className="w-full rounded-lg border border-slate-300 p-2.5 text-sm focus:border-blue-500 focus:outline-none" data-id="element-3243">
                  <option data-id="element-3244">Meeting</option>
                  <option data-id="element-3245">Meal</option>
                  <option data-id="element-3246">Transport</option>
                  <option data-id="element-3247">Activity</option>
                  <option data-id="element-3248">Note</option>
                </select>
              </div>
              <div data-id="element-3249">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-3250">
                  Title
                </label>
                <input type="text" value={addForm.title} onChange={e => setAddForm({
              ...addForm,
              title: e.target.value
            })} className="w-full rounded-lg border border-slate-300 p-2.5 text-sm focus:border-blue-500 focus:outline-none" placeholder="e.g. Client lunch at The Grill" data-id="element-3251" />
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-3252">
                <div data-id="element-3253">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-3254">
                    Date
                  </label>
                  <input type="date" value={addForm.date} onChange={e => setAddForm({
                ...addForm,
                date: e.target.value
              })} className="w-full rounded-lg border border-slate-300 p-2.5 text-sm focus:border-blue-500 focus:outline-none" data-id="element-3255" />
                </div>
                <div data-id="element-3256">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-3257">
                    Time
                  </label>
                  <input type="time" value={addForm.time} onChange={e => setAddForm({
                ...addForm,
                time: e.target.value
              })} className="w-full rounded-lg border border-slate-300 p-2.5 text-sm focus:border-blue-500 focus:outline-none" data-id="element-3258" />
                </div>
              </div>
              <div data-id="element-3259">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-3260">
                  Location
                </label>
                <input type="text" value={addForm.location} onChange={e => setAddForm({
              ...addForm,
              location: e.target.value
            })} className="w-full rounded-lg border border-slate-300 p-2.5 text-sm focus:border-blue-500 focus:outline-none" placeholder="e.g. 123 Market St, SF" data-id="element-3261" />
              </div>
              <div data-id="element-3262">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-3263">
                  Cost (optional)
                </label>
                <div className="relative" data-id="element-3264">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" data-id="element-3265">
                    $
                  </span>
                  <input type="number" value={addForm.cost} onChange={e => setAddForm({
                ...addForm,
                cost: e.target.value
              })} className="w-full rounded-lg border border-slate-300 p-2.5 pl-7 text-sm focus:border-blue-500 focus:outline-none" placeholder="0.00" data-id="element-3266" />
                </div>
              </div>
              <div data-id="element-3267">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-3268">
                  Notes
                </label>
                <textarea rows={2} value={addForm.notes} onChange={e => setAddForm({
              ...addForm,
              notes: e.target.value
            })} className="w-full rounded-lg border border-slate-300 p-2.5 text-sm focus:border-blue-500 focus:outline-none" placeholder="Additional details..." data-id="element-3269" />
              </div>
              <div className="flex gap-3 pt-2" data-id="element-3270">
                <button onClick={() => setShowAddModal(false)} className="flex-1 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50" data-id="element-3271">
                  Cancel
                </button>
                <button onClick={handleAddItem} className="flex-1 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700" data-id="element-3272">
                  Add Item
                </button>
              </div>
            </div>
          </div>
        </div>}

      {/* Add Note Modal */}
      {showNoteModal && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm" data-id="element-3273">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl" data-id="element-3274">
            <div className="flex items-center justify-between mb-6" data-id="element-3275">
              <h3 className="text-lg font-bold text-slate-900" data-id="element-3276">
                Add Trip Note
              </h3>
              <button onClick={() => setShowNoteModal(false)} className="rounded-full p-1 hover:bg-slate-100" data-id="element-3277">
                <XIcon className="h-5 w-5 text-slate-500" data-id="element-3278" />
              </button>
            </div>
            <div className="space-y-4" data-id="element-3279">
              <div data-id="element-3280">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-3281">
                  Note or Preference
                </label>
                <textarea rows={3} value={newNote} onChange={e => setNewNote(e.target.value)} className="w-full rounded-lg border border-slate-300 p-2.5 text-sm focus:border-blue-500 focus:outline-none" placeholder="e.g. Prefer window seat, Vegetarian meals, Need early check-in..." data-id="element-3282" />
                <div className="flex flex-wrap gap-1.5 mt-2" data-id="element-3283">
                  {['Window seat preferred', 'Vegetarian meals', 'Need early check-in', 'Allergic to peanuts', 'Extra pillows'].map(suggestion => <button key={suggestion} onClick={() => setNewNote(suggestion)} className="text-[10px] px-2 py-1 rounded-full border border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-slate-700 transition-colors" data-id="element-3284">
                      {suggestion}
                    </button>)}
                </div>
              </div>
              <div className="flex gap-3 pt-2" data-id="element-3285">
                <button onClick={() => setShowNoteModal(false)} className="flex-1 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50" data-id="element-3286">
                  Cancel
                </button>
                <button onClick={handleAddNote} className="flex-1 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700" data-id="element-3287">
                  Save Note
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}